'use client';

import React, { useState, useMemo, useEffect } from 'react';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  Tag,
  CreditCard,
  User,
  Phone,
  Mail,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { Vehicle, Booking, ProtectionPlan } from '@/types';
import { useCurrency } from '@/components/site/CurrencyContext';
import { useLocale } from '@/lib/i18n';
import { RENTAL_LOCATIONS, RENTAL_ADD_ONS } from '@/lib/data';
import { CarIllustration } from './CarIllustrations';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVehicle: Vehicle | null;
  initialCriteria?: {
    location?: string;
    pickupDate?: string;
    pickupTime?: string;
    dropoffDate?: string;
    dropoffTime?: string;
  };
  onCompleteBooking: (newBooking: Booking) => void;
  onNavigateToProfile: () => void;
}

export function BookingModal({
  isOpen,
  onClose,
  selectedVehicle,
  initialCriteria,
  onCompleteBooking,
  onNavigateToProfile,
}: BookingModalProps) {
  const { fmt, equiv } = useCurrency();
  const { t } = useLocale();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  // Form states
  const [pickupLoc, setPickupLoc] = useState(
    initialCriteria?.location || 'Miami International Airport (MIA)'
  );
  const [dropoffLoc, setDropoffLoc] = useState(
    initialCriteria?.location || 'Miami International Airport (MIA)'
  );
  const [pickupDate, setPickupDate] = useState(
    initialCriteria?.pickupDate || '2026-10-18'
  );
  const [pickupTime, setPickupTime] = useState(
    initialCriteria?.pickupTime || '10:00 AM'
  );
  const [dropoffDate, setDropoffDate] = useState(
    initialCriteria?.dropoffDate || '2026-10-22'
  );
  const [dropoffTime, setDropoffTime] = useState(
    initialCriteria?.dropoffTime || '10:00 AM'
  );

  // Protection & Add-ons
  const [protectionPlan, setProtectionPlan] = useState<ProtectionPlan>('standard');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>(['toll-pass']);

  // Driver details
  const [customerName, setCustomerName] = useState('Alex Morgan');
  const [customerEmail, setCustomerEmail] = useState('alex.morgan@example.com');
  const [customerPhone, setCustomerPhone] = useState('+1 (555) 234-8901');
  const [driverLicense, setDriverLicense] = useState('DL-89210-CA');
  const [isAge21, setIsAge21] = useState(true);

  // Promo code & payment
  const [promoInput, setPromoInput] = useState('FLEETAVA20');
  const [appliedPromo, setAppliedPromo] = useState<string | null>('FLEETAVA20');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'counter'>('card');
  const [cardNumber, setCardNumber] = useState('•••• •••• •••• 4242');
  const [cardExpiry, setCardExpiry] = useState('12/28');
  const [cardCvc, setCardCvc] = useState('883');

  // Confirmation state
  const [confirmedBooking, setConfirmedBooking] = useState<Booking | null>(null);
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const closeBooking = React.useRef(onClose);
  closeBooking.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting) closeBooking.current();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, submitting]);

  // Live availability for the selected vehicle + dates (public check, no login).
  // Key-matched so stale results never block the form; all setState happens
  // in async callbacks (lint-safe, no sync setState in the effect body).
  const availKey =
    selectedVehicle && pickupDate && dropoffDate && dropoffDate >= pickupDate
      ? `${selectedVehicle.id}|${pickupDate}|${dropoffDate}`
      : null;
  const [avail, setAvail] = useState<{ key: string | null; status: 'checking' | 'ok' | 'taken' }>({
    key: null,
    status: 'checking',
  });
  const availForInput = avail.key === availKey ? avail.status : 'idle';
  useEffect(() => {
    if (!availKey) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setAvail({ key: availKey, status: 'checking' });
      try {
        const [vid, pick, drop] = availKey.split('|');
        const res = await fetch(
          `/api/availability?vehicleId=${encodeURIComponent(vid)}&pickupDate=${encodeURIComponent(pick)}&dropoffDate=${encodeURIComponent(drop)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setAvail({ key: availKey, status: data.available === true ? 'ok' : 'taken' });
      } catch {
        if (!cancelled) setAvail({ key: null, status: 'checking' });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [availKey]);

  // Calculation of days & cost
  const rentalDays = useMemo(() => {
    const start = new Date(pickupDate).getTime();
    const end = new Date(dropoffDate).getTime();
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 1;
  }, [pickupDate, dropoffDate]);

  const protectionDailyCost = useMemo(() => {
    if (protectionPlan === 'premium') return 220;
    if (protectionPlan === 'standard') return 140;
    return 0; // basic
  }, [protectionPlan]);

  const addOnsDailyCost = useMemo(() => {
    return selectedAddOns.reduce((total, addOnId) => {
      const item = RENTAL_ADD_ONS.find((a) => a.id === addOnId);
      return total + (item ? item.pricePerDay : 0);
    }, 0);
  }, [selectedAddOns]);

  const pricePerDay = selectedVehicle ? selectedVehicle.pricePerDay : 59;
  const quoteKey = selectedVehicle
    ? `${selectedVehicle.id}|${pickupDate}|${dropoffDate}|${protectionPlan}|${[...selectedAddOns].sort().join(',')}|${appliedPromo ?? ''}`
    : '';
  const [serverQuote, setServerQuote] = useState<{
    key: string;
    rentalDays: number;
    dailyRate: number;
    baseRental: number;
    protectionPrice: number;
    addOnsPrice: number;
    discount: number;
    totalPrice: number;
  } | null>(null);
  useEffect(() => {
    if (!quoteKey) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const [vehicleId, pick, drop, plan, addons, promo] = quoteKey.split('|');
      try {
        const res = await fetch('/api/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicleId,
            pickupDate: pick,
            dropoffDate: drop,
            protectionPlan: plan,
            selectedAddOns: addons ? addons.split(',').filter(Boolean) : [],
            promoCode: promo,
          }),
        });
        const data = await res.json().catch(() => null);
        if (!cancelled && res.ok && data && typeof data.totalPrice === 'number') {
          setServerQuote({ key: quoteKey, ...data });
        }
      } catch {
        if (!cancelled) setServerQuote(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [quoteKey]);
  const quoted = serverQuote?.key === quoteKey ? serverQuote : null;
  const pricedDays = quoted?.rentalDays ?? rentalDays;
  const pricedDaily = quoted?.dailyRate ?? pricePerDay;
  const baseRentalTotal = quoted?.baseRental ?? pricePerDay * rentalDays;
  const totalProtection = quoted?.protectionPrice ?? protectionDailyCost * rentalDays;
  const totalAddOns = quoted?.addOnsPrice ?? addOnsDailyCost * rentalDays;
  const subtotal = baseRentalTotal + totalProtection + totalAddOns;
  const discountAmount = quoted?.discount ?? (appliedPromo === 'FLEETAVA20' ? Math.round(subtotal * 0.2) : 0);
  const finalTotal = quoted?.totalPrice ?? Math.max(0, subtotal - discountAmount);

  if (!isOpen || !selectedVehicle) return null;

  const handleApplyPromo = () => {
    if (promoInput.trim().toUpperCase() === 'FLEETAVA20') {
      setAppliedPromo('FLEETAVA20');
    } else {
      alert(t('book.invalidPromo'));
    }
  };

  const toggleAddOn = (id: string) => {
    if (selectedAddOns.includes(id)) {
      setSelectedAddOns(selectedAddOns.filter((x) => x !== id));
    } else {
      setSelectedAddOns([...selectedAddOns, id]);
    }
  };

  const handleConfirmReservation = async () => {
    if (!selectedVehicle || submitting) return;
    setSubmitError(null);

    // Re-check availability right before submit (dates may have changed).
    try {
      const chk = await fetch(
        `/api/availability?vehicleId=${encodeURIComponent(selectedVehicle.id)}&pickupDate=${encodeURIComponent(pickupDate)}&dropoffDate=${encodeURIComponent(dropoffDate)}`
      );
      const chkData = await chk.json().catch(() => ({}));
      if (chkData.available !== true) {
        if (availKey) setAvail({ key: availKey, status: 'taken' });
        setSubmitError(t('book.justTaken'));
        return;
      }
    } catch {
      // Fall through and let the server decide (it enforces availability anyway).
    }

    // Test flow: create pending booking + draft contract, then redirect the
    // guest to sign the e-contract BEFORE the booking counts as submitted.
    // Signing auto-confirms the booking server-side. No client login needed.
    setSubmitting(true);
    try {
      const res = await fetch('/api/book-and-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicleId: selectedVehicle.id,
          customerName,
          customerEmail,
          customerPhone,
          driverLicense,
          pickupLocation: pickupLoc,
          dropoffLocation: dropoffLoc,
          pickupDate,
          pickupTime,
          dropoffDate,
          dropoffTime,
          protectionPlan,
          selectedAddOns,
          promoCode: appliedPromo || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        if (availKey) setAvail({ key: availKey, status: 'taken' });
        setSubmitError(t('book.takenErr'));
        return;
      }
      if (!res.ok || !data.booking || !data.signUrl) {
        setSubmitError(data.error ?? t('book.failed'));
        return;
      }
      const created = data.booking as Booking;
      setConfirmedBooking(created);
      onCompleteBooking(created);
      setSignUrl(data.signUrl as string);
      setStep(5);
      // Redirect to e-signature (same tab keeps the test flow simple).
      window.location.assign(data.signUrl as string);
    } catch {
      setSubmitError(t('book.network'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex bg-slate-950/40"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-title"
        onClick={(event) => event.stopPropagation()}
        className="ms-auto flex h-full w-full max-w-xl flex-col bg-white text-slate-900 shadow-2xl"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)] text-xs font-bold text-white">
              {step < 5 ? `0${step}` : '✓'}
            </div>
            <div className="min-w-0">
              <h3 id="booking-title" className="truncate text-sm font-extrabold tracking-tight text-slate-900">
                {step === 1 && t('book.s1')}
                {step === 2 && t('book.s2')}
                {step === 3 && t('book.s3')}
                {step === 4 && t('book.s4')}
                {step === 5 && t('book.s5')}
              </h3>
              <p className="truncate text-[11px] text-slate-500">
                {selectedVehicle.name} · {fmt(pricedDaily)}/day · {pricedDays} {t('book.daysTotal')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="shrink-0 cursor-pointer rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
            aria-label="Close booking"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body / Steps */}
        <div className="p-6 overflow-y-auto grow">
          {/* STEP 1: DATES & LOCATIONS */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Vehicle preview bar */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-28 h-16 bg-white rounded-xl p-1 flex items-center justify-center border border-slate-200/60 shrink-0">
                    <CarIllustration type={selectedVehicle.imageType} className="w-full h-auto" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-[var(--color-primary)] uppercase tracking-wider">
                      {selectedVehicle.category}
                    </span>
                    <h4 className="text-base font-extrabold text-slate-900">
                      {selectedVehicle.name}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {selectedVehicle.seats} {t('cat.seatsU')} · {selectedVehicle.transmission} · {selectedVehicle.bags} {t('cat.bagsU')}
                    </p>
                  </div>
                </div>
                <div className="text-right sm:border-l sm:border-slate-200 sm:pl-6">
                  <span className="text-xl font-black text-[var(--color-primary)] font-mono">
                    {fmt(selectedVehicle.pricePerDay)}
                  </span>
                  <span className="text-xs text-slate-400"> / day</span>
                  <div className="text-[11px] font-semibold text-emerald-600">
                    {t('book.unlimited')}
                  </div>
                </div>
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1.5">
                    {t('book.pickLoc')}
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <MapPin className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                    <select
                      value={pickupLoc}
                      onChange={(e) => setPickupLoc(e.target.value)}
                      className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
                    >
                      {RENTAL_LOCATIONS.map((loc) => (
                        <option key={loc.id} value={`${loc.name} (${loc.code})`}>
                          {loc.name} ({loc.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1.5">
                    {t('book.retLoc')}
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <MapPin className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                    <select
                      value={dropoffLoc}
                      onChange={(e) => setDropoffLoc(e.target.value)}
                      className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
                    >
                      {RENTAL_LOCATIONS.map((loc) => (
                        <option key={loc.id} value={`${loc.name} (${loc.code})`}>
                          {loc.name} ({loc.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1.5">
                    {t('book.pickDT')}
                  </label>
                  <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden"
                    />
                    <select
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
                    >
                      {['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '04:00 PM'].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1.5">
                    {t('book.retDT')}
                  </label>
                  <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={dropoffDate}
                      onChange={(e) => setDropoffDate(e.target.value)}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden"
                    />
                    <select
                      value={dropoffTime}
                      onChange={(e) => setDropoffTime(e.target.value)}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
                    >
                      {['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '04:00 PM'].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Live availability for these dates (public check, no login) */}
              {availForInput === 'checking' && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-500">
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-[var(--color-primary)] animate-spin" />
                  {t('book.checking')}
                </div>
              )}
              {availForInput === 'ok' && (
                <div className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/10 p-3 text-xs font-semibold text-success">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  {selectedVehicle.name} {t('book.available')} {pickupDate} → {dropoffDate}.
                </div>
              )}
              {availForInput === 'taken' && (
                <div className="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger/10 p-3 text-xs font-semibold text-danger">
                  <X className="w-4 h-4 shrink-0" />
                  {t('book.taken')}
                </div>
              )}

              {/* Free Cancellation Callout */}
              <div className="flex items-center gap-3 rounded-xl border border-warning/40 bg-warning/10 p-3.5 text-xs text-amber-950">
                <CheckCircle2 className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                <span>
                  <strong>{t('book.freeCancel')}</strong> {t('book.freeCancelD')}
                </span>
              </div>
            </div>
          )}

          {/* STEP 2: PROTECTION & ADD-ONS */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-1">
                  {t('book.protection')}
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                  {t('book.protectionD')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Basic */}
                  <div
                    onClick={() => setProtectionPlan('basic')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                      protectionPlan === 'basic'
                        ? 'border-[var(--color-primary)] bg-amber-50/40 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-slate-800">{t('book.basic')}</span>
                      <span className="text-xs font-black text-slate-900 font-mono">{fmt(0)} /day</span>
                    </div>
                    <ul className="text-[11px] text-slate-500 space-y-1.5 mt-3">
                      <li>• {t('book.basic1')}</li>
                      <li>• {fmt(10000)} security deposit</li>
                      <li>• {t('book.basic3')}</li>
                    </ul>
                  </div>

                  {/* Standard */}
                  <div
                    onClick={() => setProtectionPlan('standard')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer relative ${
                      protectionPlan === 'standard'
                        ? 'border-[var(--color-primary)] bg-amber-50/40 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-[var(--color-primary)] text-white text-[9px] font-bold uppercase tracking-wider">
                      {t('book.popular')}
                    </div>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-slate-800">{t('book.standard')}</span>
                      <span className="text-xs font-black text-[var(--color-primary)] font-mono">+{fmt(140)} /day</span>
                    </div>
                    <ul className="text-[11px] text-slate-600 space-y-1.5 mt-3">
                      <li>• {t('book.cdw')}</li>
                      <li>• {t('book.reduced')} {fmt(5000)} {t('book.deduct')}</li>
                      <li>• {t('book.theft')}</li>
                    </ul>
                  </div>

                  {/* Premium */}
                  <div
                    onClick={() => setProtectionPlan('premium')}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                      protectionPlan === 'premium'
                        ? 'border-[var(--color-primary)] bg-amber-50/40 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-bold text-slate-800">{t('book.premium')}</span>
                      <span className="text-xs font-black text-[var(--color-primary)] font-mono">+{fmt(220)} /day</span>
                    </div>
                    <ul className="text-[11px] text-slate-600 space-y-1.5 mt-3">
                      <li>• {fmt(0)} {t('book.zeroDed')}</li>
                      <li>• {t('book.glass')}</li>
                      <li>• {t('book.roadside')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Add-ons List */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-bold text-slate-900 mb-3">
                  {t('book.addons')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {RENTAL_ADD_ONS.map((addon) => {
                    const isChecked = selectedAddOns.includes(addon.id);
                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddOn(addon.id)}
                        className={`p-3.5 rounded-xl border flex items-start justify-between gap-3 cursor-pointer transition-all ${
                          isChecked
                            ? 'border-[var(--color-primary)] bg-amber-50/30'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="mt-0.5 accent-[var(--color-primary)]"
                          />
                          <div>
                            <div className="text-xs font-bold text-slate-800">{addon.name}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                              {addon.description}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-[var(--color-primary)] font-mono shrink-0">
                          +{fmt(addon.pricePerDay)}/day
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: DRIVER DETAILS */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    {t('book.driver')}
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                    <input
                      type="text"
                      required
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    {t('book.email')}
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                    <input
                      type="email"
                      required
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    {t('book.phone')}
                  </label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                    <input
                      type="tel"
                      required
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    {t('book.license')}
                  </label>
                  <input
                    type="text"
                    required
                    value={driverLicense}
                    onChange={(e) => setDriverLicense(e.target.value)}
                    placeholder="e.g. DL-89210-CA"
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
              </div>

              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={isAge21}
                    onChange={(e) => setIsAge21(e.target.checked)}
                    className="accent-[var(--color-primary)]"
                  />
                  <span>
                    {t('book.age')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* STEP 4: REVIEW & PAYMENT */}
          {step === 4 && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Payment Details (Span 7) */}
              <div className="lg:col-span-7 space-y-4">
                <h4 className="text-sm font-bold text-slate-900">
                  {t('book.payMethod')}
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`p-3 rounded-xl border flex items-center gap-2 text-xs font-bold cursor-pointer transition-all ${
                      paymentMethod === 'card'
                        ? 'border-[var(--color-primary)] bg-amber-50/40 text-slate-900'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <CreditCard className="w-4 h-4 text-[var(--color-primary)]" />
                    <span>{t('book.card')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('counter')}
                    className={`p-3 rounded-xl border flex items-center gap-2 text-xs font-bold cursor-pointer transition-all ${
                      paymentMethod === 'counter'
                        ? 'border-[var(--color-primary)] bg-amber-50/40 text-slate-900'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <MapPin className="w-4 h-4 text-[var(--color-primary)]" />
                    <span>{t('book.counter')}</span>
                  </button>
                </div>

                {paymentMethod === 'card' && (
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-3">
                    <div>
                      <label className="text-[11px] font-bold text-slate-600 block mb-1">
                        {t('book.cardNum')}
                      </label>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900"
                      />
                    </div>
                    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px] font-bold text-slate-600 block mb-1">
                          {t('book.exp')}
                        </label>
                        <input
                          type="text"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(e.target.value)}
                          className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-slate-600 block mb-1">
                          {t('book.cvc')}
                        </label>
                        <input
                          type="text"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value)}
                          className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-900"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Promo Code Box */}
                <div className="bg-amber-50/50 border border-amber-200/80 p-3.5 rounded-2xl">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900 mb-1.5">
                    <Tag className="w-3.5 h-3.5 text-[var(--color-primary)]" />
                    <span>{t('book.promo')}</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. FLEETAVA20"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      className="grow px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs font-mono text-slate-900 uppercase"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      className="px-4 py-1.5 bg-[var(--color-primary)] text-white text-xs font-bold rounded-lg hover:bg-[var(--color-primary-dark)] cursor-pointer"
                    >
                      {t('book.apply')}
                    </button>
                  </div>
                  {appliedPromo && (
                    <div className="text-[11px] text-emerald-600 font-bold mt-1.5 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      <span>{t('book.appliedCode')} &quot;{appliedPromo}&quot; {t('book.applied')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Summary Receipt (Span 5) */}
              <div className="lg:col-span-5 bg-slate-50 border border-slate-200 p-5 rounded-2xl space-y-3">
                <h4 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-2">
                  {t('book.summary')}
                </h4>

                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>{selectedVehicle.name} ({pricedDays} days @ {fmt(pricedDaily)})</span>
                    <span className="font-mono font-medium">{fmt(baseRentalTotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>{t('book.protLabel')} ({protectionPlan.toUpperCase()})</span>
                    <span className="font-mono font-medium">{fmt(totalProtection)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>{t('book.addonsLbl')} ({selectedAddOns.length} {t('book.selected')})</span>
                    <span className="font-mono font-medium">{fmt(totalAddOns)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-semibold">
                      <span>{t('book.promoDisc')}</span>
                      <span className="font-mono">-{fmt(discountAmount)}</span>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-200 flex justify-between items-baseline">
                  <span className="text-sm font-extrabold text-slate-900">{t('book.totalDue')}</span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-[var(--color-primary)] font-mono">
                      {fmt(finalTotal)}
                    </span>
                    <div className="text-[10px] text-slate-400">{t('book.taxes')}</div>
                    {equiv(finalTotal) ? (
                      <div className="text-[11px] text-slate-400">{equiv(finalTotal)} {t('book.forInfo')}</div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: E-SIGNATURE REDIRECT (test flow) */}
          {step === 5 && confirmedBooking && (
            <div className="text-center py-6 max-w-lg mx-auto space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <div>
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-mono font-bold">
                  Reference: {confirmedBooking.id}
                </span>
                <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight mt-2">
                  {t('book.signTitle')}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {t('book.signD')}
                </p>
              </div>

              {submitError && (
                <div className="rounded-xl border border-danger/30 bg-danger/10 p-3 text-xs font-semibold text-danger">
                  {submitError}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                {signUrl ? (
                  <a
                    href={signUrl}
                    className="grow px-6 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all text-center"
                  >
                    {t('book.signNow')}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className="grow px-6 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    {t('book.done')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onNavigateToProfile();
                  }}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer"
                >
                  {t('book.viewProfile')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Bottom Navigation Actions */}
        {step < 5 && (
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between shrink-0">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s - 1) as any)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>{t('book.back')}</span>
              </button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s + 1) as any)}
                className="flex items-center gap-2 px-6 py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer"
              >
                <span>{t('book.continue')}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConfirmReservation}
                disabled={submitting || availForInput === 'taken' || availForInput === 'checking'}
                title={
                  availForInput === 'taken'
                    ? t('book.notAvailTitle')
                    : availForInput === 'checking'
                      ? t('book.checkingTitle')
                      : t('book.confirmTitle')
                }
                className="flex items-center gap-2 px-8 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white text-xs font-extrabold uppercase tracking-wider rounded-xl shadow-lg transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{submitting ? t('book.booking') : t('book.confirm')}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {step === 4 && submitError && (
          <div className="border-t border-danger/30 bg-danger/10 px-6 py-3 text-xs font-semibold text-danger">
            {submitError}
          </div>
        )}
      </div>
    </div>
  );
}
